import React, { useCallback, useEffect, useMemo, useState } from "react";
import config from "../config.js";
import { apiErrorMessage } from '../utils/apiError';
import { loadCustody } from '../features/materials/materialData';

/**
 * MATERIAL AT A VENDOR — stock that is still ours but not on our floor.
 *
 * On-hand means "on our floor". Driving tube to a laser house splits one event into two, days
 * apart: it leaves here on issue, and comes back as components on receipt. In between, what we
 * paid for was invisible — not on hand, not on order, not anywhere. A custody row is that
 * middle state.
 *
 * ⚠ The number this card refuses against is AVAILABLE, not on-hand, and available nets out the
 * reservation this material's own free-issue demand created. Without that netting the gate read
 * `on_hand - reserved` and refused every quantity on exactly the materials free issue is for —
 * the nightly reconciler reserves the sticks the vendor will need, and the shipment that serves
 * that demand was then blocked by it. Every OTHER reservation still blocks, which is the point.
 *
 * ⚠⚠ "STILL HOLDING" IS NOT THE OPERATIONAL QUESTION. "20, of which 1 is coming back" is. We
 * send a spare stick on purpose — Kemlee's plan needs 19 of the 20 driven over — and before the
 * expected/back columns existed, the 20th was indistinguishable from steel the vendor still
 * needed. It sat "at vendor" forever because nobody could tell it was owed. Every position now
 * shows what the POs say the vendor should cut, and what that leaves.
 */
export default function MaterialCustodyCard({ materialId, materialCode, onChanged }) {
  const API_BASE = config.API_BASE_URL;

  const [rows, setRows] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);
  const [showIssue, setShowIssue] = useState(false);
  const [showClosed, setShowClosed] = useState(false);
  const [expanded, setExpanded] = useState(null);      // custody_id whose history is open
  const [returningId, setReturningId] = useState(null); // custody_id whose return form is open
  const [form, setForm] = useState({ vendor_id: "", quantity: "", po_number: "", notes: "", pin: "" });
  const blankReturn = { quantity: "", disposition: "stock", notes: "", pin: "" };
  const [ret, setRet] = useState(blankReturn);

  const load = useCallback(async () => {
    if (!materialId) return;
    setLoading(true);
    setError("");
    try {
      setRows(await loadCustody(materialId)); // features/materials/materialData (the phone's too)
    } catch (err) {
      setError(`Could not load custody: ${err.message}`);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [materialId]);

  useEffect(() => { load(); }, [load]);

  const loadVendors = useCallback(async () => {
    if (vendors.length) return;
    try {
      const res = await fetch(`${API_BASE}/vendors`);
      if (res.ok) setVendors(await res.json());
    } catch { /* the select degrades to empty; the server still refuses a bad vendor */ }
  }, [API_BASE, vendors.length]);

  const open = useMemo(() => rows.filter((r) => !r.closed_at), [rows]);
  const closed = useMemo(() => rows.filter((r) => r.closed_at), [rows]);
  const atVendors = useMemo(
    () => open.reduce((s, r) => s + Number(r.quantity || 0), 0), [open]);
  // Sums only over positions that HAVE a plan — a position with no PO behind it reports
  // expected_back as null, and coercing that to 0 would quietly claim nothing is owed.
  const owedBack = useMemo(
    () => open.reduce((s, r) => s + (r.expected_back == null ? 0 : Number(r.expected_back)), 0),
    [open]);

  const openDocument = (eventId) => {
    window.open(`${API_BASE}/material_custody/events/${eventId}/document`, "_blank", "noopener");
  };

  const issue = async () => {
    setBusy(true);
    setError("");
    setNotice("");
    try {
      const res = await fetch(`${API_BASE}/materials/${materialId}/issue_to_vendor`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          vendor_id: Number(form.vendor_id),
          quantity: Number(form.quantity),
          po_number: form.po_number ? Number(form.po_number) : null,
          notes: form.notes || null,
          pin: form.pin || null,
        }),
      });
      // The refusal text explains WHICH number blocked the issue (available vs on-hand vs
      // reserved). Truncating it to a generic "failed" would throw away the only diagnosis.
      if (!res.ok) throw new Error(await apiErrorMessage(res));
      const out = await res.json();
      setShowIssue(false);
      setForm({ vendor_id: "", quantity: "", po_number: "", notes: "", pin: "" });
      await load();
      if (onChanged) onChanged();
      // The transfer document travels with the truck, so it opens NOW rather than waiting for
      // the operator to find it in a history panel they may never open.
      if (out.event_id) {
        setNotice(
          `Transfer ${out.document_number} recorded${out.expected_back != null
            ? ` — the plan uses ${out.expected_to_use}, so ${out.expected_back} should come back`
            : ""}. Opening the transfer document to print and send with the load.`);
        openDocument(out.event_id);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const submitReturn = async (r) => {
    setBusy(true);
    setError("");
    setNotice("");
    try {
      const res = await fetch(`${API_BASE}/material_custody/${r.custody_id}/return`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          quantity: Number(ret.quantity),
          disposition: ret.disposition,
          notes: ret.notes || null,
          pin: ret.pin || null,
        }),
      });
      if (!res.ok) throw new Error(await apiErrorMessage(res));
      const out = await res.json();
      setReturningId(null);
      setRet(blankReturn);
      await load();
      if (onChanged) onChanged();
      setNotice(out.disposition === "stock"
        ? `${out.returned} back on the shelf — ${out.material_id ? "on hand " : ""}`
          + `${out.on_hand_before} → ${out.on_hand_after}. ${out.vendor_name} still holds `
          + `${out.still_at_vendor}. Receipt ${out.document_number}.`
        : `${out.returned} written off at ${out.vendor_name} — stock is unchanged (the `
          + `material was expensed when it left the floor). ${out.vendor_name} still holds `
          + `${out.still_at_vendor}. Record ${out.document_number}.`);
      if (out.event_id) openDocument(out.event_id);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  if (!materialId) return null;

  const dt = (v) => (v ? new Date(v).toLocaleDateString() : "—");
  const qty = (v) => Number(v ?? 0).toLocaleString(undefined, { maximumFractionDigits: 3 });
  // ⚠ null is NOT zero here. A position with no PO behind it has no basis for a plan, and
  // rendering that as "0 expected back" would read as "the vendor keeps everything".
  const plan = (v) => (v == null ? <span className="text-muted">—</span> : qty(v));

  const startReturn = (r) => {
    setReturningId(r.custody_id);
    setError("");
    setNotice("");
    // Pre-fill what the plan says is owed, capped at what the vendor is actually holding.
    // It is the number the operator is nearly always typing, and it is right there.
    const suggested = r.expected_back == null
      ? "" : String(Math.min(Number(r.expected_back), Number(r.quantity || 0)));
    setRet({ ...blankReturn, quantity: suggested });
  };

  const row = (r) => {
    const isOpen = !r.closed_at;
    const events = r.events || [];
    return (
      <React.Fragment key={r.custody_id}>
        <tr className={r.closed_at ? "text-muted" : ""}>
          <td>
            <strong>{r.vendor_name || `Vendor #${r.vendor_id}`}</strong>
            {r.po_number ? <div className="small">PO {r.po_number}</div> : null}
            {r.job_id ? <div className="small">Job {r.job_id}</div> : null}
          </td>
          <td className="text-end">
            <span className="fs-5">{qty(r.quantity)}</span>
            <div className="small text-muted">of {qty(r.issued_quantity)} issued</div>
          </td>
          <td className="text-end">
            {plan(r.expected_to_use)}
            {r.still_to_use != null && Number(r.still_to_use) > 0 && (
              <div className="small text-muted">{qty(r.still_to_use)} still to cut</div>
            )}
          </td>
          <td className="text-end">
            {r.expected_back != null && Number(r.expected_back) > 0 && isOpen ? (
              <span className="badge bg-info text-dark fs-6">{qty(r.expected_back)}</span>
            ) : plan(r.expected_back)}
            {Number(r.returned || 0) > 0 && (
              <div className="small text-success">{qty(r.returned)} returned</div>
            )}
            {Number(r.written_off || 0) > 0 && (
              <div className="small text-danger">{qty(r.written_off)} written off</div>
            )}
          </td>
          <td className="text-end">
            {/* A part-consumed stick is real: 3 of 4 pieces back leaves the stick spent but the
                position still holding whole sticks. Showing the pieces keeps that honest. */}
            {Number(r.pieces_consumed || 0) > 0 ? qty(r.pieces_consumed) : "—"}
          </td>
          <td>{dt(r.opened_at)}</td>
          <td>{r.closed_at ? dt(r.closed_at) : <span className="badge bg-warning text-dark">At vendor</span>}</td>
          <td className="text-end text-nowrap">
            {isOpen && Number(r.quantity) > 0 && (
              <button className="btn btn-sm btn-outline-success me-1"
                      onClick={() => startReturn(r)} disabled={busy}>
                Receive back
              </button>
            )}
            {events.length > 0 && (
              <button className="btn btn-sm btn-link px-1"
                      onClick={() => setExpanded(expanded === r.custody_id ? null : r.custody_id)}>
                {expanded === r.custody_id ? "Hide" : `Paperwork (${events.length})`}
              </button>
            )}
          </td>
        </tr>

        {returningId === r.custody_id && (
          <tr>
            <td colSpan={8} className="bg-light">
              <div className="p-2">
                <div className="row g-2 align-items-end">
                  <div className="col-md-2">
                    <label className="form-label small mb-1">Quantity *</label>
                    <input type="number" step="0.001" min="0" max={r.quantity}
                           className="form-control form-control-sm" value={ret.quantity}
                           onChange={(e) => setRet({ ...ret, quantity: e.target.value })} />
                    <div className="form-text">{qty(r.quantity)} at the vendor</div>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label small mb-1">What happened to it? *</label>
                    <select className="form-select form-select-sm" value={ret.disposition}
                            onChange={(e) => setRet({ ...ret, disposition: e.target.value })}>
                      <option value="stock">Came back — put it on the shelf</option>
                      <option value="writeoff">Used or scrapped at the vendor — not coming back</option>
                    </select>
                  </div>
                  <div className="col-md-3">
                    <label className="form-label small mb-1">Notes</label>
                    <input className="form-control form-control-sm" value={ret.notes}
                           placeholder={ret.disposition === "writeoff" ? "e.g. bad cuts on setup" : ""}
                           onChange={(e) => setRet({ ...ret, notes: e.target.value })} />
                  </div>
                  <div className="col-md-1">
                    <label className="form-label small mb-1">PIN</label>
                    <input className="form-control form-control-sm" value={ret.pin}
                           onChange={(e) => setRet({ ...ret, pin: e.target.value })} />
                  </div>
                  <div className="col-md-2 text-end">
                    <button className="btn btn-sm btn-success me-1" onClick={() => submitReturn(r)}
                            disabled={busy || !(Number(ret.quantity) > 0)}>
                      {busy ? "Saving…" : "Record"}
                    </button>
                    <button className="btn btn-sm btn-outline-secondary"
                            onClick={() => { setReturningId(null); setRet(blankReturn); }}
                            disabled={busy}>
                      Cancel
                    </button>
                  </div>
                </div>
                {/* The two options are NOT the same event and the difference is money. Say so
                    here rather than letting an operator discover it in the ledger. */}
                <div className={`alert py-2 small mt-2 mb-0 ${
                  ret.disposition === "writeoff" ? "alert-warning" : "alert-info"}`}>
                  {ret.disposition === "writeoff"
                    ? "Stock will NOT go up — this material was expensed when it left our floor, "
                      + "so booking it back in only to book it out again would count the loss twice. "
                      + "The position closes down and the write-off is recorded against this vendor."
                    : "Stock goes up by this amount and the ledger records the receipt, exactly "
                      + "mirroring the issue that sent it out."}
                </div>
              </div>
            </td>
          </tr>
        )}

        {expanded === r.custody_id && events.length > 0 && (
          <tr>
            <td colSpan={8} className="bg-light">
              <div className="p-2">
                <div className="small text-muted mb-2">
                  Every movement a person made on this position. Consumption is not listed here —
                  components arriving draw the position down on their own, and their trail is the
                  purchase order receipt.
                </div>
                <table className="table table-sm mb-0">
                  <thead>
                    <tr>
                      <th>Document</th><th>Type</th><th className="text-end">Qty</th>
                      <th>PO</th><th>Date</th><th>Notes</th><th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {events.map((e) => (
                      <tr key={e.event_id}>
                        <td><code>{e.document_number}</code></td>
                        <td>
                          {e.event_type === "issue" && <span className="badge bg-warning text-dark">Issued out</span>}
                          {e.event_type === "return" && <span className="badge bg-success">Returned to stock</span>}
                          {e.event_type === "writeoff" && <span className="badge bg-danger">Written off</span>}
                        </td>
                        <td className="text-end">{qty(e.quantity)}</td>
                        <td className="small">{e.po_number || "—"}</td>
                        <td className="small">{dt(e.created_at)}</td>
                        <td className="small">{e.notes || ""}</td>
                        <td className="text-end">
                          <button className="btn btn-sm btn-outline-primary"
                                  onClick={() => openDocument(e.event_id)}>
                            Print
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </td>
          </tr>
        )}
      </React.Fragment>
    );
  };

  return (
    <div className="card mb-3 border-warning">
      <div className="card-header bg-warning bg-opacity-10 d-flex justify-content-between align-items-center">
        <span>
          <strong>At vendors</strong>{" "}
          <span className="text-muted small">— our material, off our floor</span>
          {atVendors > 0 && (
            <span className="badge bg-warning text-dark ms-2">{qty(atVendors)} out</span>
          )}
          {owedBack > 0 && (
            <span className="badge bg-info text-dark ms-2">{qty(owedBack)} expected back</span>
          )}
        </span>
        {!showIssue && (
          <button className="btn btn-sm btn-outline-warning"
                  onClick={() => { setShowIssue(true); loadVendors(); }} disabled={busy}>
            Issue to vendor
          </button>
        )}
      </div>
      <div className="card-body">
        {error && <div className="alert alert-danger py-2 small mb-2" style={{ whiteSpace: "pre-wrap" }}>{error}</div>}
        {notice && <div className="alert alert-success py-2 small mb-2">{notice}</div>}
        {loading && <div className="text-muted small">Loading…</div>}

        {showIssue && (
          <div className="border rounded p-3 bg-light mb-3">
            <div className="row g-2">
              <div className="col-md-5">
                <label className="form-label small mb-1">Vendor *</label>
                <select className="form-select form-select-sm" value={form.vendor_id}
                        onChange={(e) => setForm({ ...form, vendor_id: e.target.value })}>
                  <option value="">— pick a vendor —</option>
                  {vendors.map((v) => (
                    <option key={v.vendor_id} value={v.vendor_id}>{v.name}</option>
                  ))}
                </select>
                <div className="form-text">
                  Whoever physically receives it. Receipts draw down THIS vendor's position, so
                  the wrong name here books the consumption against the wrong company.
                </div>
              </div>
              <div className="col-md-3">
                <label className="form-label small mb-1">Quantity (sticks) *</label>
                <input type="number" step="0.001" min="0" className="form-control form-control-sm"
                       value={form.quantity}
                       onChange={(e) => setForm({ ...form, quantity: e.target.value })} />
              </div>
              <div className="col-md-3">
                <label className="form-label small mb-1">Against PO # (optional)</label>
                <input type="number" className="form-control form-control-sm"
                       placeholder="e.g. 1338"
                       value={form.po_number}
                       onChange={(e) => setForm({ ...form, po_number: e.target.value })} />
                <div className="form-text">
                  The order the vendor is cutting against — it is what tells us how much of this
                  load they should use, and therefore how much is owed back.
                </div>
              </div>
              <div className="col-md-1">
                <label className="form-label small mb-1">PIN</label>
                <input className="form-control form-control-sm" value={form.pin}
                       onChange={(e) => setForm({ ...form, pin: e.target.value })} />
                <div className="form-text">Released by</div>
              </div>
              <div className="col-12">
                <label className="form-label small mb-1">Notes</label>
                <input className="form-control form-control-sm" value={form.notes}
                       onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </div>
            </div>
            <div className="alert alert-info py-2 small mt-3 mb-0">
              This moves {materialCode ? <strong>{materialCode}</strong> : "the material"} off our
              floor now — stock drops and the ledger records the issue. It comes back as
              components, not as this material, so nothing returns it to stock automatically.
              A <strong>transfer document</strong> is generated for the vendor to sign; it opens
              for printing as soon as this is saved.
            </div>
            <div className="mt-3">
              <button className="btn btn-sm btn-warning me-2" onClick={issue}
                      disabled={busy || !form.vendor_id || !(Number(form.quantity) > 0)}>
                {busy ? "Issuing…" : "Issue to vendor"}
              </button>
              <button className="btn btn-sm btn-outline-secondary"
                      onClick={() => { setShowIssue(false); setError(""); }} disabled={busy}>
                Cancel
              </button>
            </div>
          </div>
        )}

        {!loading && !open.length && !closed.length && (
          <div className="text-muted small mb-0">
            None of this material is at a vendor. Issue some when we supply the stock for a
            component a vendor makes for us.
          </div>
        )}

        {(open.length > 0 || (showClosed && closed.length > 0)) && (
          <div className="table-responsive">
            <table className="table table-sm align-middle mb-0">
              <thead>
                <tr>
                  <th>Vendor</th>
                  <th className="text-end">Still holding</th>
                  <th className="text-end">Should use</th>
                  <th className="text-end">Expected back</th>
                  <th className="text-end">Pieces made</th>
                  <th>Issued</th>
                  <th>Closed</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {open.map(row)}
                {showClosed && closed.map(row)}
              </tbody>
            </table>
          </div>
        )}

        {closed.length > 0 && (
          <button className="btn btn-sm btn-link px-0 mt-2"
                  onClick={() => setShowClosed(!showClosed)}>
            {showClosed ? "Hide" : `Show ${closed.length} closed position(s)`}
          </button>
        )}
      </div>
    </div>
  );
}
